/**
 * El círculo con iniciales y, montado encima, el canal por donde llegó.
 *
 * El canal venía como badge de texto (`WHATSAPP`, `TELEGRAM`) al lado del
 * nombre: robaba el ancho que necesita el nombre y obligaba a leer para saber
 * algo que un ícono resuelve de un vistazo. Acá el dato viaja en el avatar, que
 * es donde el ojo ya está mirando.
 *
 * Los colores son los de marca de cada canal a propósito — son los que el
 * operador reconoce sin pensar — y por eso no salen de los tokens del design
 * system, que describen a Tibo y no a WhatsApp.
 */

interface Props {
  /** Nombre para las iniciales; si falta, se usa el identificador. */
  nombre: string;
  canal: string;
}

const CANALES: Record<string, { etiqueta: string; fondo: string; icono: JSX.Element }> = {
  whatsapp: {
    etiqueta: 'WhatsApp',
    fondo: '#25D366',
    icono: (
      <path
        fill="currentColor"
        d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.9-4.45 9.9-9.91C21.95 6.45 17.5 2 12.04 2Zm5.8 14.16c-.24.68-1.42 1.3-1.95 1.35-.5.05-.99.23-3.35-.7-2.82-1.11-4.6-3.97-4.74-4.15-.14-.19-1.13-1.5-1.13-2.86 0-1.36.71-2.03.96-2.31.25-.28.55-.35.73-.35h.52c.17 0 .4-.06.62.48.24.57.8 1.97.87 2.11.07.14.12.31.02.5-.09.19-.14.31-.28.47l-.42.49c-.14.14-.28.29-.12.57.16.28.72 1.19 1.55 1.93 1.06.95 1.96 1.24 2.24 1.38.28.14.44.12.6-.07.17-.19.7-.81.88-1.09.19-.28.37-.23.63-.14.26.09 1.65.78 1.93.92.28.14.47.21.54.33.07.11.07.66-.17 1.34Z"
      />
    ),
  },
  telegram: {
    etiqueta: 'Telegram',
    fondo: '#229ED9',
    icono: (
      <path
        fill="currentColor"
        d="M11.99 2c-5.52 0-10 4.48-10 10s4.48 10 10 10 10-4.48 10-10-4.48-10-10-10Zm4.64 6.84-1.55 7.32c-.12.52-.42.65-.85.4l-2.35-1.73-1.13 1.09c-.13.13-.24.24-.48.24l.17-2.43 4.42-4c.19-.17-.04-.27-.3-.1l-5.46 3.44-2.35-.74c-.51-.16-.52-.51.11-.76l9.19-3.54c.42-.15.8.1.66.81Z"
      />
    ),
  },
};

function iniciales(nombre: string): string {
  const partes = nombre
    .trim()
    .split(/\s+/)
    .filter((p) => /\p{L}/u.test(p));
  // Un identificador puro (un teléfono) no tiene iniciales que valgan: se
  // muestran los dos últimos dígitos, que es por donde la gente los distingue.
  if (partes.length === 0) return nombre.replace(/\D/g, '').slice(-2) || '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

export function ChannelAvatar({ nombre, canal }: Props) {
  const info = CANALES[canal.toLowerCase()];

  return (
    <div className="relative shrink-0">
      <div className="w-9 h-9 rounded-full bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-200 flex items-center justify-center text-xs font-semibold select-none">
        {iniciales(nombre)}
      </div>

      {info ? (
        <span
          title={info.etiqueta}
          className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center ring-2 ring-white dark:ring-neutral-900"
          style={{ backgroundColor: info.fondo, color: '#fff' }}
        >
          <svg viewBox="0 0 24 24" className="w-3 h-3" role="img" aria-label={info.etiqueta}>
            {info.icono}
          </svg>
        </span>
      ) : (
        <span className="sr-only">{canal}</span>
      )}
    </div>
  );
}
